import React, { useState, useEffect } from 'react';
import { Button, Table, Badge, Spinner, Alert, Card } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import ProgramForm from './ProgramForm';

function ProgramManager({ onBack }) {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [startingProgram, setStartingProgram] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workoutAPI.fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingProgram(null);
    setShowForm(true);
  };

  const handleEdit = async (program) => {
    try {
      // Fetch full program details including workouts
      const fullProgram = await workoutAPI.fetchProgramById(program.id);
      setEditingProgram(fullProgram);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching program details:', err);
      setError('Failed to load program details. Please try again.');
    }
  };

  const handleSave = async (formData) => {
    setError(null);
    try {
      if (editingProgram) {
        await workoutAPI.updateProgram(editingProgram.id, formData);
      } else {
        await workoutAPI.createProgram(formData);
      }
      setShowForm(false);
      setEditingProgram(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error saving program:', err);
      setError('Failed to save program. Please try again.');
    }
  };

  const handleDelete = async (programId) => {
    if (!deleteConfirm || deleteConfirm !== programId) {
      setDeleteConfirm(programId);
      return;
    }

    setError(null);
    try {
      await workoutAPI.deleteProgram(programId);
      setDeleteConfirm(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      setError('Failed to delete program. Please try again.');
    }
  };

  const handleStartProgram = async (program) => {
    // Prevent double-click/rapid clicks
    if (startingProgram) return;

    setStartingProgram(program.id);
    setError(null);
    setSuccessMessage(null);

    try {
      // Copy the program
      const copyResult = await workoutAPI.copyProgram(program.id);

      // Start the copied program (make it active)
      await workoutAPI.startProgram(copyResult.program_id);

      // Show success message
      setSuccessMessage(`Program "${program.name}" started successfully! Go to the landing page to continue.`);

      // Refresh programs list to show the new copy
      await fetchPrograms();
    } catch (err) {
      console.error('Error starting program:', err);
      setError('Failed to start program. Please try again.');
    } finally {
      setStartingProgram(null);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading programs...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading programs...</p>
      </div>
    );
  }

  return (
    <div className="program-manager">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <i className="fas fa-calendar-week me-2"></i>
            Workout Programs
          </h2>
          <p className="text-muted">Create and manage your multi-week training programs</p>
        </div>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      <div className="mb-4">
        <Button variant="primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>
          Create New Program
        </Button>
      </div>

      {programs.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-calendar-week fa-4x text-muted mb-3"></i>
            <h4>No Programs Yet</h4>
            <p className="text-muted">
              Create your first workout program to organize your training into a structured plan
            </p>
            <Button variant="primary" onClick={handleCreateNew}>
              <i className="fas fa-plus me-2"></i>
              Create Program
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="programs-list">
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Workouts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program.id}>
                  <td>
                    <strong>{program.name}</strong>
                  </td>
                  <td>
                    <span className="text-muted">
                      {program.description || 'No description'}
                    </span>
                  </td>
                  <td>
                    <Badge bg="info">
                      {program.workout_count || 0} workout{(program.workout_count || 0) !== 1 ? 's' : ''}
                    </Badge>
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleStartProgram(program)}
                        disabled={startingProgram === program.id}
                        title="Creates your own copy of this program and activates it so you can start tracking workouts"
                      >
                        {startingProgram === program.id ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-1"
                            />
                            Starting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-play me-1"></i>
                            Start
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(program)}
                        title="Edit program"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant={deleteConfirm === program.id ? 'danger' : 'outline-danger'}
                        size="sm"
                        onClick={() => handleDelete(program.id)}
                        title={deleteConfirm === program.id ? 'Click again to confirm' : 'Delete program'}
                      >
                        <i className={`fas ${deleteConfirm === program.id ? 'fa-exclamation-triangle' : 'fa-trash'}`}></i>
                        {deleteConfirm === program.id && <span className="ms-1">Sure?</span>}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {showForm && (
        <ProgramForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setEditingProgram(null);
            setError(null);
          }}
          onSave={handleSave}
          program={editingProgram}
          error={error}
        />
      )}
    </div>
  );
}

export default ProgramManager;
